import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { authAPI } from "../services/api";

export default function UserLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Use authAPI which automatically detects environment
      const res = await authAPI.login(form.email, form.password);

      // authAPI.login already stores token and user in localStorage
      // Just need to navigate
      const user = authAPI.getCurrentUser();
      
      console.log("Login successful:", user);

      // Small delay to ensure localStorage is flushed before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Users go to user banking portal
      navigate("/user-banking");
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginCard}>
        <div style={styles.header}>
          <div style={styles.logoIcon}>🏦</div>
          <h1 style={styles.title}>Internet Banking</h1>
          <p style={styles.subtitle}>Sign in to your account</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Enter your email"
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Enter your password"
              style={styles.input}
              required
            />
          </div>

          <button type="submit" disabled={loading} style={styles.submitBtn}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            Don't have an account? <Link to="/register" style={styles.link}>Register</Link>
          </p>
        </div>

        <div style={styles.divider}>
          <span style={styles.dividerLine}></span>
          <span style={styles.dividerText}>Other Logins</span>
          <span style={styles.dividerLine}></span>
        </div>

        <div style={styles.otherLogins}>
          <Link to="/agent-login" style={styles.otherLoginBtn}>
            👤 Agent Login
          </Link>
          <Link to="/login" style={styles.otherLoginBtn}>
            🔐 Admin Login
          </Link>
        </div>
      </div>

      <div style={styles.features}>
        <div style={styles.featureItem}>
          <span style={styles.featureIcon}>💸</span>
          <span>Easy Transfers</span>
        </div>
        <div style={styles.featureItem}>
          <span style={styles.featureIcon}>📱</span>
          <span>QR Payments</span>
        </div>
        <div style={styles.featureItem}>
          <span style={styles.featureIcon}>🔒</span>
          <span>Secure Banking</span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  loginCard: {
    backgroundColor: "white",
    borderRadius: "20px",
    padding: "40px",
    width: "100%",
    maxWidth: "400px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)"
  },
  header: {
    textAlign: "center",
    marginBottom: "30px"
  },
  logoIcon: {
    fontSize: "56px",
    marginBottom: "15px"
  },
  title: {
    margin: "0 0 8px",
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e3c72"
  },
  subtitle: {
    margin: 0,
    color: "#666",
    fontSize: "14px"
  },
  error: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "12px 15px",
    borderRadius: "8px",
    marginBottom: "20px",
    fontSize: "14px",
    textAlign: "center"
  },
  form: {},
  formGroup: {
    marginBottom: "20px"
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#333"
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    border: "2px solid #e9ecef",
    borderRadius: "10px",
    fontSize: "15px",
    boxSizing: "border-box",
    transition: "border-color 0.2s"
  },
  submitBtn: {
    width: "100%",
    padding: "15px",
    background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "10px"
  },
  footer: {
    textAlign: "center",
    marginTop: "25px"
  },
  footerText: {
    margin: 0,
    color: "#666",
    fontSize: "14px"
  },
  link: {
    color: "#1e3c72",
    textDecoration: "none",
    fontWeight: "600"
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: "25px 0",
    gap: "10px"
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    backgroundColor: "#e9ecef"
  },
  dividerText: {
    color: "#999",
    fontSize: "12px"
  },
  otherLogins: {
    display: "flex",
    gap: "10px"
  },
  otherLoginBtn: {
    flex: 1,
    textAlign: "center",
    padding: "10px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    color: "#666",
    textDecoration: "none",
    fontSize: "12px",
    border: "1px solid #e9ecef"
  },
  features: {
    display: "flex",
    gap: "30px",
    marginTop: "30px",
    color: "white"
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    opacity: 0.9
  },
  featureIcon: {
    fontSize: "20px"
  }
};
